import { inject } from "@angular/core";
import { CanActivateFn, Router, UrlTree } from "@angular/router";
import { catchError, forkJoin, map, Observable, of } from "rxjs";
import { ROUTE } from "../../../shared/utils/routes";
import { RadarMarketsService } from "../../../features/radar-markets/services/radar-markets.service";
import { LicenceAccessService } from "../../services/licence-access/licence-access.service";
import { LicenseUsagesService } from "../../services/license-usages/license-usages.service";

/**
 * Dove si atterra entrando nella piattaforma.
 *
 * Di regola la dashboard. Ma **durante la settimana di prova la dashboard è
 * chiusa**: chi ci arriva trova un riquadro di blocco, cioè la porta in
 * faccia come prima cosa dopo aver fatto l'accesso. Se quella persona ha già
 * un Radar, è quella la pagina che le appartiene, ed è lì che va mandata.
 *
 * Tre condizioni, tutte necessarie: la prova in corso, un Radar arrivato in
 * fondo, e nessun errore per strada. Con tutte e tre si va al **risultato**
 * del Radar, che è la pagina dove sta il lavoro. Se una qualsiasi manca si va
 * in dashboard — **non si cambia la destinazione di qualcuno per una risposta
 * che non è arrivata**, e un atterraggio che dipende da due chiamate deve
 * sbagliare verso quello che c'era prima.
 *
 * A **prova finita** si va al Radar senza altre condizioni, e per una ragione
 * più forte: lì la dashboard non è chiusa per il pacchetto, è chiusa e basta,
 * quindi mandarcelo vorrebbe dire mostrargli un riquadro di blocco come prima
 * cosa dopo l'accesso. Il Radar invece è suo e resta aperto — ed è la pagina
 * che gli dice che cosa è successo e come riparte.
 */
/** La risposta che non è arrivata, tenuta distinta dal Radar che non c'è */
const NON_LETTO = "non-letto" as const;

export const landingGuard: CanActivateFn = (): Observable<UrlTree> => {
  const router = inject(Router);
  const licenseUsagesService = inject(LicenseUsagesService);
  const radarMarketsService = inject(RadarMarketsService);

  const dashboard = router.parseUrl(ROUTE.DASHBOARD);

  return forkJoin({
    usage: licenseUsagesService
      .getMyUsages(false)
      .pipe(catchError(() => of(undefined))),
    /*
     * `NON_LETTO` e non `null`: i due casi si somigliano e non lo sono. Senza
     * Radar si va alla pagina della generazione, ma davanti a una chiamata
     * fallita no — non si cambia la destinazione di qualcuno per una risposta
     * che non è arrivata, e quella regola vale quanto l'altra.
     */
    radar: radarMarketsService.findMine().pipe(catchError(() => of(NON_LETTO)))
  }).pipe(
    map(({ usage, radar }) => {
      const inProva = LicenceAccessService.isRunningTrial(usage ?? undefined);
      const provaFinita = LicenceAccessService.isTrialOver(usage ?? undefined);

      /*
       * A prova finita si atterra sul **risultato** del Radar, non sull'elenco.
       *
       * Dal 30 ottobre 2026 l'elenco è chiuso e il risultato no: mandare
       * qualcuno sull'elenco vorrebbe dire aprirgli in faccia il riquadro di
       * blocco come primo schermo dopo l'accesso — l'errore che questo stesso
       * guard ha già fatto una volta con la dashboard durante la prova. Sul
       * risultato trova il suo Paese migliore e, sotto, come si riparte.
       */
      if (provaFinita) {
        return router.parseUrl(ROUTE.RADAR_MARKETS_RESULT);
      }

      if (!inProva) {
        return dashboard;
      }

      if (radar === NON_LETTO) {
        return dashboard;
      }

      /*
       * Radar finito: si atterra sul **risultato**, non sull'elenco.
       *
       * L'elenco è una pagina di servizio — una riga sola, durante la prova —
       * mentre il risultato è il lavoro: il Paese migliore, i punteggi, le
       * schede. Chi entra vuole rivedere quello, non la riga che lo apre. È
       * anche la stessa destinazione di chi la prova l'ha finita, così
       * l'atterraggio non cambia da un giorno all'altro.
       */
      if (radar?.status === "DONE") {
        return router.parseUrl(ROUTE.RADAR_MARKETS_RESULT);
      }

      /*
       * Il Radar non è arrivato in fondo: si riparte da dove si era fermato.
       *
       * Prima si finiva in dashboard, che **durante la prova è chiusa**: chi
       * aveva il Radar rotto si prendeva il riquadro di blocco come prima cosa
       * dopo l'accesso, e nessuno gli diceva che poteva rifarlo. La pagina
       * della generazione invece distingue i due casi — mostra l'avanzamento
       * se sta ancora calcolando e il «riprova» se è andata male — ed è la
       * ragione per cui si manda lì e non sul Radar, che a un calcolo in corso
       * direbbe comunque «rifallo».
       *
       * Riaprirla non fa ripartire niente per sbaglio: il servizio ricalcola
       * solo se la riga è `FAILED` o se è una `PENDING` scaduta.
       */
      return router.parseUrl(ROUTE.ONBOARDING_GENERATION);
    }),
    catchError(() => of(dashboard))
  );
};
